import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, ExternalLink, Sparkles, ListChecks, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Super-admin tool — Add celebrities from AstroDatabank URLs.
 *
 * Flow:
 *   1. Paste an astro.com/astro-databank URL (or the raw page text if
 *      astro.com's JS bot-check blocks the server-side fetch).
 *   2. Server parses birth data with regex + timezonefinder (NO LLM).
 *   3. Preview form auto-fills — admin corrects industry / notes / rise
 *      event / category before hitting Save.
 *   4. Record lands in `db.custom_celebrities`, merged with the
 *      hardcoded catalog at every read (so it appears on public
 *      /celebrities/list/{category} pages instantly).
 */

const CATEGORIES = [
  { slug: "billionaires",    label: "Billionaires" },
  { slug: "rags-to-riches",  label: "Rags to Riches" },
  { slug: "lottery-winners", label: "Lottery Winners" },
  { slug: "spiritual-gurus", label: "Spiritual Gurus" },
  { slug: "royals",          label: "Royals" },
  { slug: "internet-famous", label: "Internet Famous" },
];

// Small pure helper — takes an array of {slug, label, custom?} from the
// admin categories endpoint (or the fallback hardcoded list above) and
// appends the "+ Add new category…" sentinel row so the target-category
// dropdown always has a create affordance at the bottom.
function withNewCategoryOption(cats) {
  const rows = (cats || []).map((c) => ({ value: c.slug, label: c.label + (c.custom ? " (custom)" : "") }));
  rows.push({ value: NEW_CATEGORY_SENTINEL, label: "+ Add new category…" });
  return rows;
}

const RODDEN_RATINGS = ["AA", "A", "B", "C", "DD", "X"];

// Special sentinel value the target-category dropdown injects at the
// bottom of its option list. When picked, an inline "new category" form
// opens right below the dropdown.
const NEW_CATEGORY_SENTINEL = "__new__";

export default function AdminAddCelebrityPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sourceUrl, setSourceUrl] = useState("");
  const [rawText, setRawText] = useState("");
  const [parseBusy, setParseBusy] = useState(false);
  const [form, setForm] = useState(null);        // parsed + editable
  const [saveBusy, setSaveBusy] = useState(false);
  const [custom, setCustom] = useState([]);

  // ── Bulk category import ─────────────────────────────────────────────
  // Second input mode — admin pastes an AstroDatabank category URL (or
  // raw page text as fallback) and the server extracts every profile
  // link. Admin ticks the ones to import and hits Bulk Import; server
  // fetches + parses each in a loop and reports per-URL success/fail.
  const [catUrl, setCatUrl] = useState("");
  const [catRawText, setCatRawText] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const [scanned, setScanned] = useState([]);        // [{name, url, slug_hint, _picked?}]
  const [bulkCategory, setBulkCategory] = useState("lottery-winners");
  const [bulkIndustry, setBulkIndustry] = useState("");
  const [bulkCompany, setBulkCompany] = useState("");
  const [bulkCountry, setBulkCountry] = useState("");
  const [bulkStoryDefault, setBulkStoryDefault] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // {imported, skipped, failed}

  const isSuperAdmin = !!user?.is_super_admin;

  // Live categories from the backend (hardcoded 6 + custom-added).
  // Falls back to the local hardcoded list if the fetch fails so the
  // page still works during a network hiccup. Refreshes after creating
  // or deleting a category so the dropdown always reflects the DB.
  const [categoriesList, setCategoriesList] = useState(CATEGORIES);
  // Inline "new category" form state. Rendered right below whichever
  // category dropdown is set to NEW_CATEGORY_SENTINEL. `newCatFor`
  // remembers which field opened it (single-add form vs bulk form) so
  // we know where to auto-select the newly created slug after save.
  const [newCatFor, setNewCatFor] = useState(null);
  const [newCatSlug, setNewCatSlug] = useState("");
  const [newCatLabel, setNewCatLabel] = useState("");
  const [newCatBlurb, setNewCatBlurb] = useState("");
  const [newCatBusy, setNewCatBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isSuperAdmin) navigate("/", { replace: true });
    else {
      loadCustom();
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSuperAdmin]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/admin/celebrities/categories");
      const cats = (data.categories || []).map((c) => ({
        slug: c.slug, label: c.label, custom: !!c.custom,
      }));
      if (cats.length > 0) setCategoriesList(cats);
    } catch {
      // Keep the hardcoded fallback — the page must stay usable.
    }
  };

  // Opens the inline new-category form. `origin` is either "single"
  // (the Step-2 target-category dropdown on a single add) or "bulk"
  // (the bulk-import target-category dropdown) — we remember which so
  // we can auto-select the freshly created slug on the right dropdown
  // after the admin hits Save.
  const openNewCategoryForm = (origin) => {
    setNewCatFor(origin);
    setNewCatSlug("");
    setNewCatLabel("");
    setNewCatBlurb("");
  };

  const cancelNewCategoryForm = () => {
    // Bounce the dropdown that opened us back to its previous valid value
    // so it's not stuck on the sentinel.
    if (newCatFor === "single" && form && form.category === NEW_CATEGORY_SENTINEL) {
      setForm({ ...form, category: categoriesList[0]?.slug || "royals" });
    }
    if (newCatFor === "bulk" && bulkCategory === NEW_CATEGORY_SENTINEL) {
      setBulkCategory(categoriesList[0]?.slug || "royals");
    }
    setNewCatFor(null);
  };

  const saveNewCategory = async () => {
    const slug = (newCatSlug || "").trim().toLowerCase();
    const label = (newCatLabel || "").trim();
    if (!slug || !label) { toast.error("Slug + label are required."); return; }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      toast.error("Slug must be lowercase kebab-case (e.g. 'nobel-laureates').");
      return;
    }
    setNewCatBusy(true);
    try {
      await api.post("/admin/celebrities/categories", {
        slug, label,
        hero_title: label,
        hero_blurb: newCatBlurb || "",
      });
      toast.success(`Category '${slug}' saved.`);
      await loadCategories();
      // Auto-select the newly created slug on whichever dropdown opened the form.
      if (newCatFor === "single" && form) setForm({ ...form, category: slug });
      if (newCatFor === "bulk") setBulkCategory(slug);
      setNewCatFor(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save category.");
    } finally {
      setNewCatBusy(false);
    }
  };

  // Category dropdown handler that intercepts the sentinel and opens
  // the inline new-category form instead of setting the sentinel as
  // the actual category.
  const onCategoryChange = (setter, origin) => (val) => {
    if (val === NEW_CATEGORY_SENTINEL) {
      openNewCategoryForm(origin);
    }
    // Set the value either way — the SelectField will render the sentinel
    // in its dropdown, but the inline form below owns the correction on
    // save/cancel via `newCatFor`.
    setter(val);
  };

  const loadCustom = async () => {
    try {
      const { data } = await api.get("/admin/celebrities/custom");
      setCustom(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load custom celebrities.");
    }
  };

  const parseSource = async () => {
    if (!sourceUrl.trim() && !rawText.trim()) {
      toast.error("Paste an astro.com URL or raw page text.");
      return;
    }
    setParseBusy(true);
    try {
      const { data } = await api.post("/admin/celebrities/scrape-preview", {
        source_url: sourceUrl.trim() || null,
        raw_text: rawText.trim() || null,
      });
      const p = data.parsed || {};
      const re = p.rise_event || null;
      setForm({
        name: p.name || "",
        dob: p.dob || "",
        time: p.time || "12:00",
        place: p.place || "",
        tz: p.tz || "Etc/UTC",
        country: "",
        industry: "",
        company: "",
        rodden_rating: p.rodden_rating || "X",
        photo: p.photo || "",
        // Prefill notes from the scraped biography so charts have
        // substantive context — admin can edit / trim before saving.
        notes: p.biography || "",
        category: "royals",
        source_url: p.source_url || sourceUrl.trim() || null,
        // Auto-extract from biography sentences that mention a date at
        // least 15y after the DOB — that's the celebrity's defining
        // event (lottery win, election, IPO, marriage, death). Falls
        // back to blank when no such date exists so admin knows to type
        // one in — otherwise the Varshaphal defaults to the birth year
        // which is almost never what you want for a "rise event" chart.
        rise_event_date: re?.date || "",
        rise_event_label: re?.label || "",
        rise_event_context: re?.context || "",
        story_text: "",
      });
      toast.success("Parsed — review and edit before saving.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const tech = detail && (/BrowserType\.launch|Browser logs|playwright/i.test(String(detail)) || String(detail).length > 140);
      toast.error(tech
        ? "Server browser fetch failed — use the raw-text fallback below, or retry in a minute."
        : (detail || "Could not parse the source."));
      // Auto-suggest fallback if astro.com blocked the fetch.
      if (detail && (String(detail).includes("browser-check") || /Browser fallback failed|Chromium/i.test(String(detail)))) {
        toast.info("Open the URL in your browser, select-all, copy, and paste into the 'Raw page text' box below.", { duration: 8000 });
      }
    } finally {
      setParseBusy(false);
    }
  };

  const saveCelebrity = async () => {
    if (!form) return;
    if (!form.name || !form.dob || !form.place || !form.tz || !form.category) {
      toast.error("Name, DOB, place, timezone and category are all required.");
      return;
    }
    setSaveBusy(true);
    try {
      const { data } = await api.post("/admin/celebrities/add", form);
      toast.success(`Saved — ${data.slug}`);
      setForm(null);
      setSourceUrl("");
      setRawText("");
      loadCustom();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the celebrity.");
    } finally {
      setSaveBusy(false);
    }
  };

  const deleteCustom = async (slug) => {
    if (!window.confirm(`Delete ${slug}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/celebrities/custom/${slug}`);
      toast.success(`Deleted ${slug}`);
      loadCustom();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
    }
  };

  const scanCategory = async () => {
    if (!catUrl.trim() && !catRawText.trim()) {
      toast.error("Paste a category URL or the raw category-page text.");
      return;
    }
    setScanBusy(true);
    setScanned([]);
    setBulkResult(null);
    try {
      const { data } = await api.post("/admin/celebrities/scan-category", {
        category_url: catUrl.trim() || null,
        raw_text: catRawText.trim() || null,
      });
      const rows = (data.profiles || []).map((p) => ({ ...p, _picked: true }));
      setScanned(rows);
      toast.success(`Found ${data.count} profiles — untick any you don't want.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Raw Playwright crash dumps are huge and unreadable in a toast —
      // collapse anything technical into one actionable line.
      const tech = detail && (/BrowserType\.launch|Browser logs|playwright/i.test(String(detail)) || String(detail).length > 140);
      toast.error(tech
        ? "Server browser fetch failed — use the raw-text fallback below, or retry in a minute."
        : (detail || "Could not scan the category."));
      if (detail && (String(detail).includes("browser-check") || /Browser fallback failed|Chromium/i.test(String(detail)))) {
        toast.info("Open the category URL in your browser, select-all, copy, then paste into the raw-text box.", { duration: 8000 });
      }
    } finally {
      setScanBusy(false);
    }
  };

  const toggleAllScanned = (picked) => {
    setScanned((rows) => rows.map((r) => ({ ...r, _picked: picked })));
  };

  const togglePick = (idx) => {
    setScanned((rows) => rows.map((r, i) => (i === idx ? { ...r, _picked: !r._picked } : r)));
  };

  // Progress state for the bulk-import loop. Tracks how many URLs the
  // client-side loop has processed vs total, plus the current one being
  // fetched for the status label. `bulkAbortRef` is a mutable ref so
  // that clicking Cancel mid-batch is actually visible inside the async
  // loop — a plain useState value is captured in the loop's closure and
  // never re-reads after each iteration.
  const [bulkProgress, setBulkProgress] = useState(null); // {done, total, current}
  const bulkAbortRef = React.useRef(false);

  const runBulkImport = async () => {
    const picked = scanned.filter((r) => r._picked);
    if (picked.length === 0) {
      toast.error("Nothing ticked to import.");
      return;
    }
    if (!bulkCategory) {
      toast.error("Pick a target category first.");
      return;
    }
    // Reset state for a fresh run.
    setBulkBusy(true);
    bulkAbortRef.current = false;
    setBulkResult(null);
    setBulkProgress({ done: 0, total: picked.length, current: "" });
    // Aggregate report — populated one URL at a time so the UI can
    // show live imported/skipped/failed counts as the loop runs.
    const agg = { imported: [], skipped: [], failed: [], imported_count: 0, skipped_count: 0, failed_count: 0 };
    // Loop the picked URLs one at a time so the frontend gets per-URL
    // progress feedback (a single big POST would hang for minutes with
    // no visible progress on a 200-URL batch). The backend already
    // accepts a single-URL list — no server changes needed.
    const commonPayload = {
      category: bulkCategory,
      industry_default: bulkIndustry || "",
      company_default: bulkCompany || "",
      country_default: bulkCountry || "",
      story_text_default: bulkStoryDefault || null,
      skip_existing: true,
      limit: 1,
    };
    let aborted = false;
    for (let i = 0; i < picked.length; i++) {
      if (bulkAbortRef.current) { aborted = true; break; }
      const row = picked[i];
      setBulkProgress({ done: i, total: picked.length, current: row.name });
      try {
        const { data } = await api.post("/admin/celebrities/bulk-import", {
          ...commonPayload,
          urls: [row.url],
        });
        // Merge this URL's outcome into the aggregate.
        agg.imported.push(...(data.imported || []));
        agg.skipped.push(...(data.skipped || []));
        agg.failed.push(...(data.failed || []));
        agg.imported_count += data.imported_count || 0;
        agg.skipped_count += data.skipped_count || 0;
        agg.failed_count += data.failed_count || 0;
      } catch (err) {
        // Network / auth failure — record so the admin can retry that
        // specific URL later; don't abort the whole batch.
        agg.failed.push({ url: row.url, reason: err?.response?.data?.detail || err?.message || "Request failed." });
        agg.failed_count += 1;
      }
      // Keep the running aggregate visible so the report auto-fills as
      // each URL finishes — no need to wait until the loop is over.
      setBulkResult({ ...agg });
    }
    setBulkProgress({ done: picked.length, total: picked.length, current: aborted ? "Cancelled" : "" });
    setBulkBusy(false);
    bulkAbortRef.current = false;
    const suffix = aborted ? " (cancelled)" : "";
    toast.success(`Imported ${agg.imported_count} · Skipped ${agg.skipped_count} · Failed ${agg.failed_count}${suffix}`);
    loadCustom();
  };

  const cancelBulkImport = () => {
    bulkAbortRef.current = true;
    toast.info("Stopping after the current URL finishes…");
  };

  if (!user) return null;
  if (!isSuperAdmin) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-add-celebrity-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-slate-50 hover:bg-slate-100 flex items-center justify-center transition"
          data-testid="admin-celebs-back-btn"
        >
          <ArrowLeft size={18} className="text-slate-700" />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Add Celebrity from AstroDatabank</h1>
          <p className="text-xs text-slate-500">Zero LLM spend · Regex + BeautifulSoup + timezonefinder</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/admin/delete-celebrity")}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-100 text-rose-800 text-[11px] font-bold tracking-wider hover:bg-rose-200"
          data-testid="go-delete-celebrity-btn"
        >
          <Trash2 size={12} /> DELETE TOOL
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Inline "New category" form — rendered when either the single-
            add or bulk-import category dropdown was set to the sentinel.
            Sits above everything so it can't be missed. */}
        {newCatFor && (
          <section className="bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="celeb-new-cat-form">
            <h2 className="font-semibold text-slate-900 text-lg">Create new category</h2>
            <p className="text-slate-600 text-sm mt-1">
              A new tab on the public /celebrities hub. Slug goes into URLs
              (<code className="bg-white/60 px-1 rounded text-[11px]">/celebrities/list/&lt;slug&gt;</code>).
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField
                label="Slug (lowercase kebab-case, e.g. 'nobel-laureates')"
                value={newCatSlug}
                onChange={setNewCatSlug}
                testId="new-cat-slug"
              />
              <TextField
                label="Display label"
                value={newCatLabel}
                onChange={setNewCatLabel}
                testId="new-cat-label"
              />
              <TextField
                label="Hero blurb (optional)"
                value={newCatBlurb}
                onChange={setNewCatBlurb}
                testId="new-cat-blurb"
                fullWidth
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={saveNewCategory}
                disabled={newCatBusy}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2 px-5 rounded-lg text-sm"
                data-testid="new-cat-save-btn"
              >
                {newCatBusy ? "Saving…" : "Save category"}
              </button>
              <button
                onClick={cancelNewCategoryForm}
                className="text-slate-600 hover:text-slate-900 text-sm"
                data-testid="new-cat-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </section>
        )}

        {/* Step 1 — Paste source */}
        <section className="bg-white rounded-2xl p-5 shadow-sm">
          <h2 className="font-semibold text-slate-900 text-lg">1. Paste source</h2>
          <p className="text-slate-500 text-sm mt-1">
            Give it either an <code className="bg-slate-100 px-1 rounded">https://www.astro.com/astro-databank/…</code> URL,
            or if the server-side fetch fails, paste the raw page text from your browser.
          </p>
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="text-slate-700 text-sm font-medium">AstroDatabank URL</span>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://www.astro.com/astro-databank/Abney,_Katherine"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm"
                data-testid="celeb-source-url"
              />
            </label>
            <details className="text-sm">
              <summary className="cursor-pointer text-blue-600 font-medium">Fallback: paste raw page text</summary>
              <p className="text-slate-500 text-xs mt-2">
                If astro.com blocks the server fetch (JavaScript browser-check), open the URL in your browser,
                select-all, copy, and paste below.
              </p>
              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                rows={8}
                placeholder="Paste the full astrodatabank page text here…"
                className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-xs font-mono"
                data-testid="celeb-raw-text"
              />
            </details>
            <button
              onClick={parseSource}
              disabled={parseBusy}
              className="bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold py-2.5 px-5 rounded-lg text-sm transition inline-flex items-center gap-2"
              data-testid="celeb-parse-btn"
            >
              <Sparkles size={16} />
              {parseBusy ? "Parsing…" : "Parse source"}
            </button>
          </div>
        </section>

        {/* Step 2 — Review & edit */}
        {form && (
          <section className="bg-white rounded-2xl p-5 shadow-sm" data-testid="celeb-preview-form">
            <h2 className="font-semibold text-slate-900 text-lg">2. Review &amp; edit</h2>
            <p className="text-slate-500 text-sm mt-1">
              The parser gets the mechanical fields; you fill industry, category, notes and the rise event.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="celeb-field-name" />
              <TextField label="DOB (YYYY-MM-DD)" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testId="celeb-field-dob" />
              <TextField label="Time (HH:MM 24h)" value={form.time} onChange={(v) => setForm({ ...form, time: v })} testId="celeb-field-time" />
              <TextField label="Place" value={form.place} onChange={(v) => setForm({ ...form, place: v })} testId="celeb-field-place" />
              <TextField label="Timezone (IANA)" value={form.tz} onChange={(v) => setForm({ ...form, tz: v })} testId="celeb-field-tz" />
              <TextField label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testId="celeb-field-country" />
              <TextField label="Industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} testId="celeb-field-industry" />
              <TextField label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="celeb-field-company" />
              <SelectField
                label="Rodden Rating"
                value={form.rodden_rating}
                onChange={(v) => setForm({ ...form, rodden_rating: v })}
                options={RODDEN_RATINGS.map((r) => ({ value: r, label: r }))}
                testId="celeb-field-rodden"
              />
              <SelectField
                label="Category"
                value={form.category}
                onChange={onCategoryChange((v) => setForm({ ...form, category: v }), "single")}
                options={withNewCategoryOption(categoriesList)}
                testId="celeb-field-category"
              />
              <TextField label="Photo URL" value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} testId="celeb-field-photo" fullWidth />
            </div>
            <div className="mt-4">
              <label className="text-slate-700 text-sm font-medium">Notes (biography snippet)</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm"
                data-testid="celeb-field-notes"
              />
            </div>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <p className="text-slate-700 text-sm font-medium">Rise event (optional — leave blank to use DOB placeholder)</p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <TextField label="Date" value={form.rise_event_date} onChange={(v) => setForm({ ...form, rise_event_date: v })} testId="celeb-field-rise-date" />
                <TextField label="Label" value={form.rise_event_label} onChange={(v) => setForm({ ...form, rise_event_label: v })} testId="celeb-field-rise-label" />
                <TextField label="Context" value={form.rise_event_context} onChange={(v) => setForm({ ...form, rise_event_context: v })} testId="celeb-field-rise-context" />
              </div>
            </div>
            {["rags-to-riches", "lottery-winners", "spiritual-gurus", "royals", "internet-famous"].includes(form.category) && (
              <div className="mt-4">
                <label className="text-slate-700 text-sm font-medium">
                  Category story ({form.category === "rags-to-riches" ? "rags-to-riches" : form.category.replace("-", " ")})
                </label>
                <textarea
                  value={form.story_text}
                  onChange={(e) => setForm({ ...form, story_text: e.target.value })}
                  rows={2}
                  placeholder="Short story sentence surfaced on the category page card."
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm"
                  data-testid="celeb-field-story"
                />
              </div>
            )}
            <div className="mt-5 flex items-center gap-3">
              <button
                onClick={saveCelebrity}
                disabled={saveBusy}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-2.5 px-6 rounded-lg text-sm transition inline-flex items-center gap-2"
                data-testid="celeb-save-btn"
              >
                <Download size={16} />
                {saveBusy ? "Saving…" : "Save celebrity"}
              </button>
              <button
                onClick={() => setForm(null)}
                className="text-slate-600 hover:text-slate-900 text-sm"
                data-testid="celeb-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </section>
        )}

        {/* Step 3 — Bulk category import */}
        <section className="bg-white rounded-2xl p-5 shadow-sm" data-testid="celeb-bulk-section">
          <h2 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
            <ListChecks size={18} /> Bulk import from an AstroDatabank Category
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Paste a category URL like
            <code className="ml-1 bg-slate-100 px-1 rounded text-[11px]">https://www.astro.com/astro-databank/Category:Lifestyle_:_Financial_:_Gain_-_Winnings/_Lottery</code>
            and the scanner extracts every profile on the page. Untick any you don't want, pick a target
            category, and hit Import — the server fetches + parses each profile in a loop. Same
            regex-only parser, still zero LLM spend.
          </p>
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="text-slate-700 text-sm font-medium">Category URL</span>
              <input
                type="url"
                value={catUrl}
                onChange={(e) => setCatUrl(e.target.value)}
                placeholder="https://www.astro.com/astro-databank/Category:…"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm"
                data-testid="celeb-cat-url"
              />
            </label>
            <details className="text-sm">
              <summary className="cursor-pointer text-blue-600 font-medium">Fallback: paste raw category-page text</summary>
              <p className="text-slate-500 text-xs mt-2">
                If astro.com blocks the server fetch, open the category URL in your browser, select-all,
                copy, and paste below. Both plain text ("Winfrey, Oprah Gail" one per line) and pasted
                HTML view-source work.
              </p>
              <textarea
                value={catRawText}
                onChange={(e) => setCatRawText(e.target.value)}
                rows={8}
                placeholder="Paste the full category page text here…"
                className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-xs font-mono"
                data-testid="celeb-cat-raw-text"
              />
            </details>
            <button
              onClick={scanCategory}
              disabled={scanBusy}
              className="bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold py-2.5 px-5 rounded-lg text-sm transition inline-flex items-center gap-2"
              data-testid="celeb-cat-scan-btn"
            >
              <Sparkles size={16} />
              {scanBusy ? "Scanning…" : "Scan category"}
            </button>
          </div>

          {scanned.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between">
                <p className="text-slate-700 font-semibold text-sm">
                  {scanned.filter((r) => r._picked).length} of {scanned.length} selected
                </p>
                <div className="flex items-center gap-3 text-xs">
                  <button onClick={() => toggleAllScanned(true)} className="text-blue-600 hover:text-blue-800 font-medium" data-testid="celeb-cat-select-all-btn">
                    Select all
                  </button>
                  <button onClick={() => toggleAllScanned(false)} className="text-slate-500 hover:text-slate-800 font-medium" data-testid="celeb-cat-deselect-all-btn">
                    Deselect all
                  </button>
                </div>
              </div>
              {/* Bulk defaults */}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
                <SelectField
                  label="Target category"
                  value={bulkCategory}
                  onChange={onCategoryChange(setBulkCategory, "bulk")}
                  options={withNewCategoryOption(categoriesList)}
                  testId="celeb-cat-target-select"
                />
                <TextField label="Industry (default)" value={bulkIndustry} onChange={setBulkIndustry} testId="celeb-cat-industry" />
                <TextField label="Company (default)" value={bulkCompany} onChange={setBulkCompany} testId="celeb-cat-company" />
                <TextField label="Country (default)" value={bulkCountry} onChange={setBulkCountry} testId="celeb-cat-country" />
              </div>
              {["rags-to-riches", "lottery-winners", "spiritual-gurus", "royals", "internet-famous"].includes(bulkCategory) && (
                <div className="mt-3">
                  <label className="text-slate-700 text-sm font-medium">
                    Category-story default (applied to every imported entry — you can edit later)
                  </label>
                  <textarea
                    value={bulkStoryDefault}
                    onChange={(e) => setBulkStoryDefault(e.target.value)}
                    rows={2}
                    placeholder="Optional — e.g. 'Won a national-lottery jackpot after years of low-wage work.'"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm"
                    data-testid="celeb-cat-story"
                  />
                </div>
              )}
              {/* Scanned list */}
              <div className="mt-4 max-h-72 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
                {scanned.map((row, idx) => (
                  <label
                    key={row.url}
                    className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${row._picked ? "" : "opacity-60"}`}
                    data-testid={`celeb-cat-row-${idx}`}
                  >
                    <input
                      type="checkbox"
                      checked={row._picked}
                      onChange={() => togglePick(idx)}
                      className="accent-blue-600"
                      data-testid={`celeb-cat-pick-${idx}`}
                    />
                    <span className="flex-1 text-slate-800 font-medium truncate">{row.name}</span>
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-400 hover:text-slate-700 shrink-0"
                      title="Open source URL"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button
                  onClick={runBulkImport}
                  disabled={bulkBusy}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-2.5 px-6 rounded-lg text-sm transition inline-flex items-center gap-2"
                  data-testid="celeb-cat-import-btn"
                >
                  <Download size={16} />
                  {bulkBusy
                    ? `Importing… ${bulkProgress ? `${bulkProgress.done}/${bulkProgress.total}` : ""}`
                    : `Import ${scanned.filter((r) => r._picked).length} selected`}
                </button>
                {bulkBusy && (
                  <button
                    onClick={cancelBulkImport}
                    className="text-rose-600 hover:text-rose-700 font-medium text-sm"
                    data-testid="celeb-cat-cancel-btn"
                  >
                    Cancel
                  </button>
                )}
                {!bulkBusy && (
                  <p className="text-xs text-slate-400">
                    Existing slugs are skipped automatically. Failures are reported per URL.
                  </p>
                )}
              </div>

              {/* Live progress bar — fills as each URL finishes so the admin
                  can see 42/200 style feedback rather than a spinner. */}
              {bulkProgress && (bulkBusy || bulkProgress.done === bulkProgress.total) && (
                <div className="mt-4" data-testid="celeb-cat-progress">
                  <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                    <span className="font-medium">
                      {bulkBusy
                        ? `Processing ${bulkProgress.done + 1} of ${bulkProgress.total}`
                        : `Done · ${bulkProgress.total}/${bulkProgress.total}`}
                      {bulkProgress.current && bulkBusy && (
                        <span className="text-slate-400 ml-2 truncate">— {bulkProgress.current}</span>
                      )}
                    </span>
                    <span className="font-mono font-semibold text-slate-800">
                      {bulkProgress.total > 0
                        ? Math.round((bulkProgress.done / bulkProgress.total) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-200 ${bulkBusy ? "bg-emerald-500" : "bg-emerald-600"}`}
                      style={{
                        width: `${bulkProgress.total > 0 ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  {bulkResult && (
                    <div className="mt-2 flex items-center gap-4 text-[11px]">
                      <span className="text-emerald-700"><b>{bulkResult.imported_count}</b> imported</span>
                      <span className="text-amber-700"><b>{bulkResult.skipped_count}</b> skipped</span>
                      <span className="text-rose-700"><b>{bulkResult.failed_count}</b> failed</span>
                      {bulkBusy && (
                        <span className="text-slate-500">
                          · {bulkProgress.total - bulkProgress.done} pending
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {bulkResult && (
                <details className="mt-4 border border-slate-100 rounded-lg overflow-hidden" open>
                  <summary className="px-3 py-2 cursor-pointer bg-slate-50 text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <ChevronDown size={14} />
                    Import report — {bulkResult.imported_count} imported · {bulkResult.skipped_count} skipped · {bulkResult.failed_count} failed
                  </summary>
                  <div className="p-3 text-xs space-y-3 max-h-72 overflow-y-auto">
                    {bulkResult.imported_count > 0 && (
                      <div>
                        <p className="text-emerald-700 font-semibold">Imported ({bulkResult.imported_count})</p>
                        <ul className="mt-1 list-disc list-inside text-slate-700">
                          {bulkResult.imported.map((r) => <li key={r.slug}>{r.name} — <code>{r.slug}</code></li>)}
                        </ul>
                      </div>
                    )}
                    {bulkResult.skipped_count > 0 && (
                      <div>
                        <p className="text-amber-700 font-semibold">Skipped ({bulkResult.skipped_count})</p>
                        <ul className="mt-1 list-disc list-inside text-slate-600">
                          {bulkResult.skipped.map((r) => <li key={r.url}>{r.slug || r.url} — {r.reason}</li>)}
                        </ul>
                      </div>
                    )}
                    {bulkResult.failed_count > 0 && (
                      <div>
                        <p className="text-rose-700 font-semibold">Failed ({bulkResult.failed_count})</p>
                        <p className="text-slate-500 text-[11px] mt-1">
                          Most failures are astro.com's browser-check blocking the server fetch. For each
                          failed URL, open it in your browser and use the single-add flow above with the
                          "raw text" fallback.
                        </p>
                        <ul className="mt-1 list-disc list-inside text-slate-700">
                          {bulkResult.failed.map((r) => (
                            <li key={r.url}>
                              <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{r.url}</a>
                              <span className="text-slate-500 ml-1">— {r.reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>
          )}
        </section>

        {/* Existing custom celebs */}
        <section className="bg-white rounded-2xl p-5 shadow-sm">
          <h2 className="font-semibold text-slate-900 text-lg">Custom-added celebrities ({custom.length})</h2>
          <p className="text-slate-500 text-sm mt-1">
            These live in <code className="bg-slate-100 px-1 rounded">db.custom_celebrities</code>.
            The 75+ hardcoded ones (billionaires, rags-to-riches, etc.) are not shown here.
          </p>
          {custom.length === 0 ? (
            <p className="text-slate-400 text-sm mt-4 italic">No custom celebrities yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-slate-100">
              {custom.map((c) => (
                <li key={c.slug} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">{c.name} <span className="text-slate-400 text-xs">/ {c.slug}</span></p>
                    <p className="text-slate-500 text-xs">
                      {c.category} · {c.dob} · {c.place} · Rodden {c.rodden_rating}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {c.source_url && (
                      <a
                        href={c.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-500 hover:text-slate-800"
                        title="Open source URL"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                    <button
                      onClick={() => deleteCustom(c.slug)}
                      className="text-rose-600 hover:text-rose-700"
                      data-testid={`celeb-delete-${c.slug}`}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function TextField({ label, value, onChange, testId, fullWidth = false }) {
  return (
    <label className={fullWidth ? "sm:col-span-2 block" : "block"}>
      <span className="text-slate-700 text-sm font-medium">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm"
        data-testid={testId}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-slate-700 text-sm font-medium">{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm bg-white"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
